import {useQuery} from "@tanstack/react-query";
import {getMonthlyActiveItems} from "../../api/api";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import {toast} from "react-toastify";
import {useContext, useMemo, useState} from "react";
import AuthContext from "../../context/AuthContext";
import Loader from "../../components/Loader";
import RecordsNotFound from "../../components/RecordsNotFound";
import Paginator from "../../components/Paginator";

const typeLabelMap = {
    ACTIVE_PRODUCT: "Product",
    ACTIVE_ADD_ON_PRODUCT: "Add-On",
    ACTIVE_PRODUCT_GROUP: "Product Group",
    ACTIVE_CUSTOMER: "Customer",
};

function MonthlyActiveItemsTab() {
    const {refreshToken} = useContext(AuthContext);
    const [page, setPage] = useState(1);
    const [itemTypeFilter, setItemTypeFilter] = useState("");

    const {status, data, error, isFetching, refetch} = useQuery({
        queryKey: ['monthly-active-items', page],
        queryFn: () => getMonthlyActiveItems(page),
        keepPreviousData: true,
        retry: false,
        onError: (err => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }),
    });

    const filteredItems = useMemo(() => {
        const items = data?.results ?? [];
        if (!itemTypeFilter) {
            return items;
        }
        return items.filter(item => item.item_type === itemTypeFilter);
    }, [data, itemTypeFilter]);

    const itemCounts = useMemo(() => {
        return (data?.results ?? []).reduce((acc, item) => {
            acc[item.item_type] = (acc[item.item_type] ?? 0) + 1;
            return acc;
        }, {});
    }, [data]);

    return (
        <>
            {status === 'loading' && <Loader size={'default'}/>}

            {status === 'success' || error?.response?.status === 401 ? (
                <div className="mt-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <div>
                            <h4 className="mb-1">Monthly Active Items</h4>
                            <p className="text-muted mb-0">Audit the items that contributed to your monthly billing.</p>
                        </div>
                        <div className="d-flex align-items-center">
                            <label className="mb-0 mr-2">Type</label>
                            <select
                                className="form-control"
                                value={itemTypeFilter}
                                onChange={(e) => setItemTypeFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                {Object.entries(typeLabelMap).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="row mb-3">
                        {Object.entries(typeLabelMap).map(([value, label]) => (
                            <div className="col-md-6 col-xl-3 mb-2" key={value}>
                                <div className="card border mb-0">
                                    <div className="card-body py-3">
                                        <p className="text-muted mb-1">{label}</p>
                                        <h4 className="mb-0">{itemCounts[value] ?? 0}</h4>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="table-responsive">
                        <table className="table table-centered mb-0">
                            <thead className="font-13 bg-light text-muted">
                            <tr>
                                <th>Type</th>
                                <th>Item ID</th>
                                <th>Related Orders</th>
                                <th>Month</th>
                                <th>Tracked On</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredItems.length > 0 ? filteredItems.map(item => (
                                <tr key={item.id}>
                                    <td>
                                        <span className="badge badge-light-primary">
                                            {typeLabelMap[item.item_type] ?? item.item_type}
                                        </span>
                                    </td>
                                    <td className="text-muted font-13">{item.item_id}</td>
                                    <td>
                                        <span className="badge badge-light-secondary">
                                            {item.orders?.length ?? 0} orders
                                        </span>
                                    </td>
                                    <td className="text-muted font-13">{item.month}/{item.year}</td>
                                    <td className="text-muted font-13">{new Date(item.created).toLocaleString()}</td>
                                </tr>
                            )) : <RecordsNotFound/>}
                            </tbody>
                        </table>
                    </div>

                    {status === 'success' && (
                        <Paginator data={data} page={page} setPage={setPage} last={data?.last_page} loading={isFetching}/>
                    )}
                </div>
            ) : null}
        </>
    );
}

export default MonthlyActiveItemsTab;
